import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { EnvironmentService } from '@methylium/core';
import { Observable } from 'rxjs';

@Injectable()
export class FeatureGuard {
  constructor(private environmentService: EnvironmentService) {}

  public canMatch: CanMatchFn = (
    route: Route,
    segments: UrlSegment[]
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> => {
    const url = segments.join('/');

    return this.checkFeature(url);
  };

  canActivate: CanActivateFn = (
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> | Promise<boolean> => {
    const url = state.url;

    return this.checkFeature(url);
  };

  private checkFeature(url: string): boolean {
    let allowed = true;
    if (url.includes('/virtual-room')) {
      allowed = allowed && this.isVirtualRoomEnabled();
    }

    if (url.includes('/management')) {
      allowed = allowed && this.isManagementEnabled();
    }

    if (url.includes('/settings')) {
      allowed = allowed && this.isSettingsEnabled();
    }

    if (url.includes('/devices')) {
      allowed = allowed && this.isDevicesEnabled();
    }

    if (url.includes('/security')) {
      allowed = allowed && this.isSecurityEnabled();
    }

    if (url.includes('/rollout')) {
      allowed = allowed && this.isRolloutEnabled();
    }

    if (url.includes('/ireckon')) {
      allowed = allowed && this.isIReckonEnabled();
    }

    if (url.includes('/locations')) {
      allowed = allowed && this.isLocationEnabled();
    }

    if (url.includes('/housekeeping')) {
      allowed = allowed && this.isHousekeepingEnabled();
    }

    if (url.includes('/staff')) {
      allowed = allowed && this.isStaffEnabled();
    }

    if (url.includes('/frontdesk')) {
      allowed = allowed && this.isFrontDeskEnabled();
    }

    if (url.includes('/bar')) {
      allowed = allowed && this.isBarEnabled();
    }

    if (url.includes('/kiosk')) {
      allowed = allowed && this.isKioskEnabled();
    }

    return allowed;
  }

  private isLocationEnabled(): boolean {
    return this.environmentService.environment.features.locations.enabled;
  }

  private isSettingsEnabled(): boolean {
    return this.environmentService.environment.features.settings.enabled;
  }

  private isDevicesEnabled(): boolean {
    return this.environmentService.environment.features.devices.enabled;
  }

  private isSecurityEnabled(): boolean {
    return this.environmentService.environment.features.security.enabled;
  }

  private isRolloutEnabled(): boolean {
    return this.environmentService.environment.features.rollout.enabled;
  }

  private isIReckonEnabled(): boolean {
    return this.environmentService.environment.features.ireckon.enabled;
  }

  private isVirtualRoomEnabled(): boolean {
    return this.environmentService.environment.features.virtual_room.enabled;
  }

  private isManagementEnabled(): boolean {
    return this.environmentService.environment.features.management.enabled;
  }

  private isHousekeepingEnabled(): boolean {
    return this.environmentService.environment.features.housekeeping.enabled;
  }

  private isStaffEnabled(): boolean {
    return this.environmentService.environment.features.staff.enabled;
  }

  private isFrontDeskEnabled(): boolean {
    return this.environmentService.environment.features.frontdesk.enabled;
  }

  private isBarEnabled(): boolean {
    return this.environmentService.environment.features.bar.enabled;
  }

  private isKioskEnabled(): boolean {
    return this.environmentService.environment.features.kiosk.enabled;
  }
}
